import type { AnyAction } from 'redux';
import * as types from '../types/actionTypes';
import { initialState } from './initialState';

// Mirror of REVERT_MANUALLY_APPLIED_TEMPLATE from @makemydeal/dr-dash-store
// (kept inline to avoid a cross-package dependency from offer-redux — dash-types
// already type-imports from offer-redux, so any offer-redux → dash-store/dash-types
// import closes a cycle that @nx/enforce-module-boundaries blocks).
// A drift-guard test in dash-store's dealTemplateActionCreators.spec.ts asserts these match.
export const REVERT_MANUALLY_APPLIED_TEMPLATE = 'REVERT_MANUALLY_APPLIED_TEMPLATE';

export const accepted = (state: boolean = initialState.accepted, action: AnyAction) => {
    switch (action.type) {
        case types.INIT_SUCCESS:
            return action.payload.offer.accepted || false;
        case types.ACCEPT_DEAL_SUCCESS:
        case types.ACCEPT_DEAL_SET_ACCEPTED:
            return true;
        case types.RESET_OFFER:
        case types.HIDE_ACCEPTED_FLOW_ON_SUBMIT:
            return false;
        default:
            return state;
    }
};

export const createdOn = (state: string | undefined = initialState.createdOn, action: AnyAction) => {
    switch (action.type) {
        case types.INIT_SUCCESS:
        case types.OFFER_SUBMISSION_SUCCESS:
            return (action && action.payload && action.payload.offer && action.payload.offer.createdOn) || '';
        default:
            return state;
    }
};

export const dealRefId = (state: string | undefined = initialState.dealRefId, action: AnyAction) => {
    switch (action.type) {
        case types.INIT_SUCCESS:
        case types.DEALXG_GET_SUCCESS:
        case types.OFFER_SUBMISSION_SUCCESS:
            return action.payload.offer.dealRefId || '';
        case types.CREDIT_APP_SEARCH_MERGE_DEAL:
            return action.payload?.coxAutoReferences?.dealRefId || state;
        case types.UPDATE_DEAL_REF_ID:
            return action.payload.dealRefId || state;
        default:
            return state;
    }
};

export const deal_ref_id = (state: string | undefined = initialState.deal_ref_id, action: AnyAction) => {
    switch (action.type) {
        case types.INIT_SUCCESS:
        case types.OFFER_SUBMISSION_SUCCESS:
            return action.payload.offer.deal_ref_id || '';
        case types.CREDIT_APP_SEARCH_MERGE_DEAL:
            return action.payload?.coxAutoReferences?.dealRefId || state;
        case types.UPDATE_DEAL_REF_ID:
            return action.payload.dealRefId || state;
        default:
            return state;
    }
};

export const deviceType = (state: string | undefined = initialState.deviceType, action: AnyAction) => {
    switch (action.type) {
        case types.INIT_SUCCESS:
            return action.payload.offer.deviceType || '';
        default:
            return state;
    }
};

export const fsDealRefId = (state: string | undefined = initialState.fsDealRefId, action: AnyAction) => {
    switch (action.type) {
        case types.INIT_SUCCESS:
        case types.DEALXG_GET_SUCCESS:
        case types.OFFER_SUBMISSION_SUCCESS:
            return action.payload.offer.fsDealRefId || '';
        case types.CREDIT_APP_SEARCH_MERGE_DEAL:
            return action.payload?.coxAutoReferences?.dealRefId || state;
        case types.UPDATE_DEAL_REF_ID:
            return action.payload.dealRefId || state;
        default:
            return state;
    }
};

export const globalCustomerId = (state: string | undefined = initialState.globalCustomerId, action: AnyAction) => {
    switch (action.type) {
        case types.INIT_SUCCESS:
        case types.OFFER_SUBMISSION_SUCCESS:
            return action.payload.offer.globalCustomerId || '';
        default:
            return state;
    }
};

export const incompleteDealDetails = (state: any = initialState.incompleteDealDetails, action: AnyAction) => {
    switch (action.type) {
        case types.INIT_INVALID_DEAL_SUCCESS:
        case types.INIT_SUCCESS:
            return action.payload?.offer?.incompleteDealDetails ?? {};
        case types.PAYMENT_RECEIVED: {
            // R-10 latest-call-wins: a superseded (stale) response must not clear the incomplete-deal
            // details — that belongs to the fresh call. Only leaseEndpointMiddleware sets this.
            if (action.meta?.isStalePaymentResponse) {
                return state;
            }
            return {};
        }
        default:
            return state;
    }
};

export const initialized = (state: boolean = false, action: AnyAction) => {
    switch (action.type) {
        case types.INIT_OFFER:
            return true;
        default:
            return state;
    }
};

export const offerId = (state: string | undefined = initialState.dealRefId, action: AnyAction) => {
    switch (action.type) {
        case types.BOOTSTRAP_SUCCESS:
        case types.INIT_SUCCESS:
        case types.OFFER_SUBMISSION_SUCCESS: {
            const offer = action.payload.offer;
            const { id, offerId } = offer;
            return offerId || id || '';
        }
        default:
            return state;
    }
};

export const offerSource = (state: string | undefined = initialState.offerSource, action: AnyAction) => {
    switch (action.type) {
        case types.INIT_SUCCESS:
        case types.OFFER_SUBMISSION_SUCCESS: {
            return action.payload.offer.offerSource || null;
        }
        case types.SET_OFFER_SOURCE: {
            return action.payload || null;
        }
        default: {
            return state;
        }
    }
};

export const offerType = (state: string | undefined = initialState.offerType, action: AnyAction) => {
    switch (action.type) {
        case types.INIT_OFFER:
            return action.payload?.settings?.defaultOfferType || state;
        case types.UPDATED_OFFER_TYPE:
            return action.payload;
        case types.REVERT_BALLOON_DEAL_TO_BASE_FINANCE:
            return 'finance';
        case types.PAYMENT_RECEIVED: {
            const originalActionType = action.meta?.originalAction?.type;
            if (originalActionType === types.REVERT_BALLOON_DEAL_TO_BASE_FINANCE) {
                return 'finance';
            }
            return state;
        }
        case types.BOOTSTRAP_SUCCESS:
        case types.INIT_SUCCESS:
            return action.payload.offer.type;
        case types.SYNC_MESSAGE:
            return action.message.offer.offerType || state;
        default:
            return state;
    }
};

export const sessionId = (state: string | undefined = initialState.sessionId, action: AnyAction) => {
    switch (action.type) {
        case types.INIT_OFFER:
            return action.payload.sessionId || '';
        case types.REFRESH_SESSION_ID_SUCCESS:
            return action.response.sessionId;
        case types.SYNC_MESSAGE:
            return action.message.offer.sessionId || state;
        default:
            return state;
    }
};

export const settings = (state: any = initialState.settings, action: AnyAction) => {
    switch (action.type) {
        case types.INIT_OFFER:
            return { ...state, ...action.payload.settings };
        case types.SYNC_MESSAGE:
            return { ...state, ...action.message.offer.settings };
        default:
            return state;
    }
};

export const template = (state: any = initialState.template, action: AnyAction) => {
    switch (action.type) {
        case types.INIT_SUCCESS:
            // Only update template state when a template object is present in the payload.
            // If template is undefined, preserve the existing state (avoids setting undefined fields).
            if (!action.payload || action.payload.template === undefined) {
                return state;
            }
            return {
                isTemplateApplied: action.payload.template?.templateApplied,
                name: action.payload.template?.name,
                withPaymentTerms: action.payload.template?.withPaymentTerms
            };
        case REVERT_MANUALLY_APPLIED_TEMPLATE:
            return initialState.template;
        default:
            return state;
    }
};

export const dealLifecycle = (state: any = initialState.dealLifecycle, action: AnyAction) => {
    switch (action.type) {
        case types.INIT_SUCCESS:
            return action.payload.offer?.dealLifecycle || {};
        default:
            return state;
    }
};

export const vehicleInsurance = (state: any = initialState.vehicleInsurance, action: AnyAction) => {
    switch (action.type) {
        case types.INIT_SUCCESS:
            return action.payload.offer?.vehicleInsurance || {};
        default:
            return state;
    }
};
